import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Users, Target, Building2, ChevronDown, ChevronRight, Crown, Briefcase } from 'lucide-react';
import { getTeamsUnderManager } from '../utils/teamHierarchy';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtINR = (v) => { if (!v && v !== 0) return '—'; return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v); };
const fmtUSD = (v) => { if (!v && v !== 0) return '—'; return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(v); };

const OrgDetailPage = () => {
  const { orgName } = useParams();
  const navigate = useNavigate();
  const { isSuperadmin, isCeo } = useAuth();
  const canSeeAmounts = isSuperadmin || isCeo;

  const [analytics, setAnalytics] = useState([]);
  const [monthlyData, setMonthlyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedTeams, setExpandedTeams] = useState({});

  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);

  useEffect(() => { fetchData(); }, [orgName, dateFrom, dateTo]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [analyticsRes, monthlyRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/team-analytics`, { withCredentials: true }),
        axios.get(`${API_URL}/api/team-monthly-data?date_from=${dateFrom}&date_to=${dateTo}`, { withCredentials: true }),
      ]);

      const decodedOrg = decodeURIComponent(orgName);
      const orgTeams = analyticsRes.data.filter(t => t.organization === decodedOrg);
      const monthlyTeams = monthlyRes.data?.teams || [];

      const merged = orgTeams.map(t => {
        const mt = monthlyTeams.find(x => x.id === t.id);
        return { ...t, ...(mt || {}), analytics_data: t };
      });

      setAnalytics(merged);
      setMonthlyData(monthlyRes.data);
    } catch (e) { console.error('Failed to fetch org data:', e); }
    finally { setLoading(false); }
  };

  const toggleExpand = (teamId) => setExpandedTeams(prev => ({ ...prev, [teamId]: !prev[teamId] }));

  // Group teams by manager
  const managerGroups = {};
  analytics.forEach(team => {
    const mgrKey = team.manager_name || 'Unassigned';
    if (!managerGroups[mgrKey]) managerGroups[mgrKey] = { manager_name: mgrKey, manager_id: team.manager_id, teams: [] };
    managerGroups[mgrKey].teams.push(team);
  });
  const groups = Object.values(managerGroups);

  // Summary
  const totalMembers = analytics.reduce((s, t) => s + (t.target_headcount || t.member_count || 0), 0);
  const totalCompanyTarget = analytics.reduce((s, t) => s + (t.company_target || 0), 0);
  const totalReached = analytics.reduce((s, t) => s + (t.reached_target || 0), 0);
  const totalTeams = analytics.length;
  const totalManagers = groups.length;

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
    </div>
  );

  return (
    <div className="space-y-6 max-w-[1400px] mx-auto" data-testid="org-detail-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft size={18} />
          </Button>
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center shadow-lg">
            <Building2 size={24} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900" data-testid="org-name">{decodeURIComponent(orgName)}</h1>
            <p className="text-sm text-slate-500">{totalManagers} Manager{totalManagers !== 1 ? 's' : ''} — {totalTeams} Team{totalTeams !== 1 ? 's' : ''} — {totalMembers} Members</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-500">From</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-[150px] h-8 text-xs" data-testid="org-date-from" />
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-500">To</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-[150px] h-8 text-xs" data-testid="org-date-to" />
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Managers', value: totalManagers, icon: <Crown size={18} />, color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200' },
          { label: 'Teams', value: totalTeams, icon: <Users size={18} />, color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200' },
          { label: 'Members', value: totalMembers, icon: <Users size={18} />, color: 'text-indigo-700', bg: 'bg-indigo-50 border-indigo-200' },
          { label: 'Company Target', value: `${totalReached}/${totalCompanyTarget}`, icon: <Target size={18} />, color: totalReached >= totalCompanyTarget ? 'text-emerald-700' : 'text-red-700', bg: totalReached >= totalCompanyTarget ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200' },
          { label: 'Placements', value: totalReached, icon: <Briefcase size={18} />, color: 'text-slate-700', bg: 'bg-slate-50 border-slate-200' },
        ].map(card => (
          <Card key={card.label} className={`${card.bg} border`}>
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-1">
                <span className={card.color}>{card.icon}</span>
                <span className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">{card.label}</span>
              </div>
              <p className={`text-lg font-bold ${card.color}`}>{card.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Manager Groups */}
      {groups.map(group => {
        const leadTeams = getTeamsUnderManager(group);
        return (
        <div key={group.manager_name} className="space-y-3" data-testid={`manager-group-${group.manager_id}`}>
          {/* Manager Header */}
          <div className="flex items-center gap-3 px-1">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <Crown size={16} className="text-white" />
            </div>
            <div>
              <p className="font-semibold text-slate-800 cursor-pointer hover:text-amber-700 transition-colors"
                 onClick={() => navigate(`/dashboard/manager/${group.manager_id}`)}
                 data-testid={`mgr-link-${group.manager_id}`}
              >{group.manager_name}</p>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">{leadTeams.length} Team{leadTeams.length !== 1 ? 's' : ''}</p>
            </div>
          </div>

          {/* Teams under this manager (lead teams only) */}
          {leadTeams.map(team => {
            const isExpanded = expandedTeams[team.id];
            const targetPct = team.company_target > 0 ? Math.round((team.reached_target / team.company_target) * 100) : 0;
            const members = team.members || [];

            return (
              <Card key={team.id} className="border-slate-200 ml-6 overflow-hidden" data-testid={`team-card-${team.id}`}>
                <div className="p-4 cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => toggleExpand(team.id)}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {isExpanded ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
                      <div>
                        <p className="font-semibold text-slate-900">{team.name}</p>
                        <p className="text-xs text-slate-500">
                          Lead: <strong>{team.lead_name || '—'}</strong>
                          {team.assistant_manager_name && <> — Asst Mgr: <strong>{team.assistant_manager_name}</strong></>}
                          {' — '}{team.target_headcount || team.member_count || 0} members
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-5 text-xs">
                      <div className="text-center">
                        <p className="text-[9px] text-slate-400 uppercase">Company Target</p>
                        <p className={`font-bold ${targetPct >= 100 ? 'text-emerald-700' : 'text-slate-700'}`}>{team.reached_target}/{team.company_target}</p>
                      </div>
                      {canSeeAmounts && (
                        <>
                          <div className="text-center">
                            <p className="text-[9px] text-slate-400 uppercase">Incentives</p>
                            <p className="font-bold text-emerald-700">{fmtINR(team.month_incentive || team.total_incentive || 0)}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-[9px] text-slate-400 uppercase">Margin</p>
                            <p className="font-bold text-blue-700">{fmtUSD(team.total_saved_margin || 0)}</p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  {/* Progress bar */}
                  <div className="mt-2 flex items-center gap-3">
                    <div className="flex-1 bg-slate-200/60 rounded-full h-2 overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${targetPct >= 100 ? 'bg-emerald-500' : targetPct >= 50 ? 'bg-amber-500' : 'bg-red-400'}`} style={{ width: `${Math.min(targetPct, 100)}%` }} />
                    </div>
                    <span className={`text-[10px] font-semibold ${targetPct >= 100 ? 'text-emerald-700' : 'text-slate-500'}`}>{targetPct}%</span>
                  </div>
                </div>

                {/* Expanded member table */}
                {isExpanded && (
                  <div className="border-t border-slate-100 overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-slate-50 text-slate-500 uppercase tracking-wider">
                          <th className="text-left p-3 font-medium">Name</th>
                          <th className="text-left p-3 font-medium">Role</th>
                          <th className="text-center p-3 font-medium">Placements</th>
                          <th className="text-center p-3 font-medium">Approved</th>
                          <th className="text-center p-3 font-medium">Low-End</th>
                          <th className="text-center p-3 font-medium">Effective</th>
                          {canSeeAmounts && (
                            <>
                              <th className="text-right p-3 font-medium">Incentive</th>
                              <th className="text-right p-3 font-medium">Salary</th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {members.map((m, idx) => (
                          <tr key={m.id || idx} className="border-t border-slate-100 hover:bg-blue-50/50">
                            <td className="p-3">
                              <span className="font-medium text-blue-700 cursor-pointer hover:underline"
                                    onClick={() => navigate(`/dashboard/user/${m.id}`)}
                              >{m.name}</span>
                            </td>
                            <td className="p-3 capitalize text-slate-500">{m.role?.replace(/_/g, ' ')}</td>
                            <td className="p-3 text-center">{m.total_placements || 0}</td>
                            <td className="p-3 text-center font-semibold text-emerald-700">{m.approved_count || 0}</td>
                            <td className="p-3 text-center text-amber-700">{m.low_end_count || 0}</td>
                            <td className="p-3 text-center font-semibold">{m.effective_approved || 0}</td>
                            {canSeeAmounts && (
                              <>
                                <td className="p-3 text-right font-semibold text-emerald-700">{fmtINR(m.total_incentive || 0)}</td>
                                <td className="p-3 text-right text-slate-600">{fmtINR(m.salary || 0)}</td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
        );
      })}

      {groups.length === 0 && (
        <Card><CardContent className="p-8 text-center text-slate-500">No teams found for this organization.</CardContent></Card>
      )}
    </div>
  );
};

export default OrgDetailPage;
